import React, { useEffect, useState, memo, Component } from 'react';
import { Shield, Zap, Check, ArrowRight, AlertTriangle, Download, Smartphone, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { usePurchaseNotifications } from '../hooks/usePurchaseNotifications';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialCard } from '../components/TestimonialCard';
import { BonusCard } from '../components/BonusCard';
// FAQ Component
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white" data-id="element-31">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-32">
        <span className="font-medium text-gray-900 pr-4" data-id="element-33">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-34" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-35" />}
      </button>
      {isOpen && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-36">
          <p className="text-gray-700 leading-relaxed" data-id="element-37">{answer}</p>
        </div>}
    </div>;
}
export function LandingPage() {
  const {
    trackEvent
  } = useMetaPixel();
  const {
    notification,
    isVisible
  } = usePurchaseNotifications();
  const scrollToPurchase = () => {
    const element = document.getElementById('compra');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handlePurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.controlelectrico.shop/cart/42833724571732:1';
  };
  // Track ViewContent when scrolling to bonuses
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackEvent('ViewContent', {
            content_name: 'Bonuses Section'
          });
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.5
    });
    const bonusSection = document.getElementById('bonuses');
    if (bonusSection) {
      observer.observe(bonusSection);
    }
    return () => observer.disconnect();
  }, [trackEvent]);
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800" data-id="element-38">
      {/* Notification Popup */}
      {notification && <PurchaseNotification name={notification.name} city={notification.city} isVisible={isVisible} data-id="element-39" />}

      {/* Header/Nav */}
      <header className="bg-blue-900 text-white py-4 sticky top-0 z-40 shadow-md" data-id="element-40">
        <div className="container mx-auto px-4 flex justify-between items-center" data-id="element-41">
          <div className="flex items-center gap-2" data-id="element-42">
            <Zap className="text-yellow-400 fill-yellow-400 w-6 h-6" data-id="element-43" />
            <span className="font-bold text-lg tracking-tight" data-id="element-44">
              CONTROL ELÉCTRICO
            </span>
          </div>
          <button onClick={scrollToPurchase} className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold py-2 px-4 rounded-full text-sm transition-colors" data-id="element-45">
            Acceder Ahora
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-900 to-blue-800 text-white py-12 md:py-20 px-4 text-center" data-id="element-46">
        <div className="container mx-auto max-w-4xl" data-id="element-47">
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight" data-id="element-48">
            CONTROL ELÉCTRICO
          </h1>

          <div className="mb-8 flex justify-center" data-id="element-49">
            <img src="https://cdn.magicpatterns.com/uploads/tT4YArVnzq89hbKNF75uza/Para_electricistas,_tecnicos_y_mantenimiento_industrial.png" alt="Control Eléctrico Libro Digital" className="w-full max-w-md md:max-w-lg drop-shadow-2xl hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={scrollToPurchase} data-id="element-50" />
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-yellow-400 mb-8 uppercase tracking-wide" data-id="element-51">
            Diagramas y circuitos de mando
          </h2>

          <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 mb-10" data-id="element-52">
            <p className="text-lg md:text-xl font-medium leading-relaxed" data-id="element-53">
              Si trabajás como electricista, técnico o en mantenimiento
              industrial, dejame hacerte una pregunta directa:
            </p>
          </div>

          <button onClick={scrollToPurchase} className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-xl md:text-2xl font-bold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all flex items-center gap-2 mx-auto animate-pulse" data-id="element-54">
            Sí, Quiero Entender los Circuitos <ArrowRight className="w-6 h-6" data-id="element-55" />
          </button>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 px-4 bg-white" data-id="element-56">
        <div className="container mx-auto max-w-3xl" data-id="element-57">
          <div className="prose prose-lg mx-auto text-gray-700" data-id="element-58">
            <p className="text-xl font-medium mb-8 border-l-4 border-yellow-500 pl-4" data-id="element-59">
              ¿Alguna vez miraste un diagrama eléctrico y sentiste que "más o
              menos" lo entendías… pero no del todo?
            </p>

            <p className="mb-6" data-id="element-60">
              Sabés qué es un contactor. Sabés qué hace un térmico. Sabés cómo
              conectar un pulsador.
            </p>

            <p className="mb-6 font-bold text-blue-900" data-id="element-61">
              Pero cuando mirás el circuito completo, la lógica no termina de
              cerrar.
            </p>

            <p className="mb-12" data-id="element-62">
              Y ese pequeño "no estar 100% seguro" es exactamente lo que genera
              dudas, errores y estrés en el trabajo.
            </p>

            <h3 className="text-2xl font-bold text-blue-900 mb-6 flex items-center gap-2" data-id="element-63">
              <AlertTriangle className="text-yellow-500" data-id="element-64" />
              El problema no es que no sepas trabajar
            </h3>

            <p className="mb-6" data-id="element-65">
              La mayoría de los electricistas no falla por falta de capacidad.
              Falla porque nadie le enseñó a entender la lógica real del control
              eléctrico.
            </p>

            <p className="mb-4" data-id="element-66">Aprendimos:</p>
            <ul className="list-none space-y-2 mb-6 pl-4" data-id="element-67">
              <li className="flex items-center gap-2" data-id="element-68">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-69"></div>circuitos
                de memoria
              </li>
              <li className="flex items-center gap-2" data-id="element-70">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-71"></div>esquemas
                copiados
              </li>
              <li className="flex items-center gap-2" data-id="element-72">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-73"></div>"esto
                siempre se hizo así"
              </li>
            </ul>

            <p className="mb-4" data-id="element-74">Pero cuando cambia algo:</p>
            <ul className="list-none space-y-2 mb-8 pl-4" data-id="element-75">
              <li className="flex items-center gap-2" data-id="element-76">
                <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-77"></div>el
                sistema
              </li>
              <li className="flex items-center gap-2" data-id="element-78">
                <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-79"></div>la carga
              </li>
              <li className="flex items-center gap-2" data-id="element-80">
                <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-81"></div>el tipo
                de tablero
              </li>
            </ul>

            <p className="text-xl font-bold text-red-600 mb-12 text-center" data-id="element-82">
              aparece la inseguridad. Y la inseguridad, en este rubro, se paga
              caro.
            </p>
          </div>
        </div>
      </section>

      {/* Fear Section */}
      <section className="py-16 px-4 bg-gray-100" data-id="element-83">
        <div className="container mx-auto max-w-3xl" data-id="element-84">
          <h3 className="text-2xl font-bold text-blue-900 mb-8 text-center" data-id="element-85">
            El miedo silencioso que nadie dice
          </h3>

          <div className="grid md:grid-cols-2 gap-6 mb-10" data-id="element-86">
            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-500" data-id="element-87">
              <p className="font-medium text-gray-800" data-id="element-88">
                miedo a quemar un motor
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-500" data-id="element-89">
              <p className="font-medium text-gray-800" data-id="element-90">
                miedo a que el circuito no responda como debería
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-500" data-id="element-91">
              <p className="font-medium text-gray-800" data-id="element-92">
                miedo a quedar mal frente al cliente
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-500" data-id="element-93">
              <p className="font-medium text-gray-800" data-id="element-94">
                miedo a perder tiempo, dinero o reputación
              </p>
            </div>
          </div>

          <p className="text-center text-lg text-gray-700 mb-8" data-id="element-95">
            No porque no sepas conectar… sino porque no entendés completamente
            el porqué del circuito.
          </p>

          <div className="text-center" data-id="element-96">
            <button onClick={scrollToPurchase} className="bg-blue-900 hover:bg-blue-800 text-white font-bold py-3 px-8 rounded-lg shadow-md transition-colors inline-flex items-center gap-2" data-id="element-97">
              Quiero Superar Estos Miedos <ArrowRight className="w-5 h-5" data-id="element-98" />
            </button>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 px-4 bg-white" data-id="element-99">
        <div className="container mx-auto max-w-3xl" data-id="element-100">
          <div className="prose prose-lg mx-auto text-gray-700" data-id="element-101">
            <h3 className="text-2xl font-bold text-blue-900 mb-6" data-id="element-102">
              ¿Por qué pasa esto?
            </h3>
            <p className="mb-6" data-id="element-103">
              Porque la mayoría de las soluciones no resuelven el problema de
              fondo.
            </p>
            <p className="mb-8" data-id="element-104">
              Los libros teóricos explican demasiado en abstracto. Los videos
              muestran casos aislados. La experiencia sola funciona… hasta que
              el sistema cambia.
            </p>

            <div className="bg-blue-50 p-8 rounded-xl border border-blue-100 mb-12" data-id="element-105">
              <p className="text-xl font-bold text-blue-900 text-center" data-id="element-106">
                En ningún lado te enseñan a pensar el control eléctrico como un
                sistema completo.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-blue-900 mb-6" data-id="element-107">
              La verdad que cambia todo
            </h2>
            <p className="mb-6" data-id="element-108">
              El problema no es falta de información. El problema es falta de
              estructura y lógica aplicada.
            </p>
            <p className="mb-6" data-id="element-109">
              La mayoría aprende componentes sueltos y esquemas aislados. Pero
              nadie explica cómo conectar:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-8 font-medium" data-id="element-110">
              <li data-id="element-111">el diagrama</li>
              <li data-id="element-112">la función</li>
              <li data-id="element-113">la aplicación real</li>
            </ul>
            <p className="mb-12" data-id="element-114">de forma clara y práctica.</p>
          </div>
        </div>
      </section>

      {/* Product Intro Section */}
      <section className="py-20 px-4 bg-blue-900 text-white relative overflow-hidden" data-id="element-115">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/circuit-board.png')] opacity-10" data-id="element-116"></div>
        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-117">
          <div className="text-center mb-12" data-id="element-118">
            <span className="bg-yellow-500 text-blue-900 font-bold px-4 py-1 rounded-full text-sm uppercase tracking-wider mb-4 inline-block" data-id="element-119">
              La Solución Definitiva
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-120">
              Ahí nace CONTROL ELÉCTRICO
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 max-w-2xl mx-auto" data-id="element-121">
              Control Eléctrico es un manual práctico creado para ayudarte a:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center mb-12" data-id="element-122">
            <div className="space-y-4" data-id="element-123">
              {['entender la lógica de los circuitos de mando', 'leer diagramas con seguridad', 'aplicar lo aprendido en motores, sistemas y tableros reales', 'dejar de memorizar esquemas y empezar a comprenderlos'].map((item, index) => <div key={index} className="flex items-start gap-3 bg-blue-800/50 p-4 rounded-lg border border-blue-700" data-id="element-124">
                  <Check className="text-yellow-400 w-6 h-6 flex-shrink-0 mt-0.5" data-id="element-125" />
                  <span className="text-lg" data-id="element-126">{item}</span>
                </div>)}
            </div>
            <div className="flex justify-center" data-id="element-127">
              <img src="https://cdn.magicpatterns.com/uploads/tT4YArVnzq89hbKNF75uza/Para_electricistas,_tecnicos_y_mantenimiento_industrial.png" alt="Manual Control Eléctrico" className="w-full max-w-sm drop-shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-128" />
            </div>
          </div>

          <div className="text-center max-w-3xl mx-auto" data-id="element-129">
            <p className="text-lg mb-8 italic" data-id="element-130">
              "Este material funciona como un traductor entre el diagrama y el
              tablero real. No se trata de aprender más cosas. Se trata de
              entender mejor lo que ya usás todos los días."
            </p>
            <button onClick={scrollToPurchase} className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-xl font-bold py-4 px-10 rounded-lg shadow-lg hover:shadow-xl transition-all" data-id="element-131">
              Conseguir Control Eléctrico Ahora
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-white" data-id="element-132">
        <div className="container mx-auto max-w-3xl" data-id="element-133">
          <div className="prose prose-lg mx-auto text-gray-700" data-id="element-134">
            <h3 className="text-2xl font-bold text-blue-900 mb-6" data-id="element-135">
              Cuando entendés la lógica, todo cambia
            </h3>
            <p className="mb-6" data-id="element-136">
              El diagrama deja de ser un dibujo confuso. El tablero deja de
              generar dudas. El trabajo se vuelve más rápido, más seguro y más
              profesional.
            </p>
            <p className="mb-4 font-bold" data-id="element-137">Podés:</p>
            <ul className="list-none space-y-3 mb-8 pl-0" data-id="element-138">
              <li className="flex items-center gap-3" data-id="element-139">
                <Shield className="text-blue-600 w-5 h-5" data-id="element-140" /> adaptar circuitos
              </li>
              <li className="flex items-center gap-3" data-id="element-141">
                <Shield className="text-blue-600 w-5 h-5" data-id="element-142" /> tomar decisiones
                con criterio
              </li>
              <li className="flex items-center gap-3" data-id="element-143">
                <Shield className="text-blue-600 w-5 h-5" data-id="element-144" /> trabajar con
                tranquilidad al energizar
              </li>
            </ul>
            <p className="text-xl font-bold text-blue-900 mb-12 text-center" data-id="element-145">
              Y eso se nota.
            </p>

            <div className="bg-gray-50 p-8 rounded-xl border-l-4 border-yellow-500 mb-12" data-id="element-146">
              <h4 className="text-xl font-bold mb-4" data-id="element-147">Pensalo así</h4>
              <p className="mb-4" data-id="element-148">
                Muchos electricistas trabajan como manejar de memoria un camino.
              </p>
              <p className="mb-4" data-id="element-149">
                Mientras el recorrido es siempre el mismo, funciona. Pero cuando
                cambian las calles… te perdés.
              </p>
              <p className="font-bold text-lg" data-id="element-150">
                Control Eléctrico no te da un camino fijo. Te da el mapa.
              </p>
              <p className="italic" data-id="element-151">
                Y cuando tenés el mapa, no importa el recorrido.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-16 px-4 bg-gray-100" data-id="element-152">
        <div className="container mx-auto max-w-4xl text-center" data-id="element-153">
          <h3 className="text-3xl font-bold text-blue-900 mb-10" data-id="element-154">
            ¿Para quién es este material?
          </h3>
          <div className="grid md:grid-cols-2 gap-6 mb-12 text-left" data-id="element-155">
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow" data-id="element-156">
              <div className="flex items-start gap-4" data-id="element-157">
                <div className="bg-blue-100 p-3 rounded-full" data-id="element-158">
                  <Zap className="text-blue-600 w-6 h-6" data-id="element-159" />
                </div>
                <div data-id="element-160">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-161">
                    Sos electricista o técnico eléctrico
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-162">
                    Que busca profesionalizar su trabajo diario.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow" data-id="element-163">
              <div className="flex items-start gap-4" data-id="element-164">
                <div className="bg-blue-100 p-3 rounded-full" data-id="element-165">
                  <Zap className="text-blue-600 w-6 h-6" data-id="element-166" />
                </div>
                <div data-id="element-167">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-168">
                    Trabajás con motores y tableros
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-169">
                    Y necesitás entender a fondo los sistemas de mando.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow" data-id="element-170">
              <div className="flex items-start gap-4" data-id="element-171">
                <div className="bg-blue-100 p-3 rounded-full" data-id="element-172">
                  <Zap className="text-blue-600 w-6 h-6" data-id="element-173" />
                </div>
                <div data-id="element-174">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-175">
                    Querés dejar de improvisar
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-176">
                    Y tener un método claro de trabajo.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow" data-id="element-177">
              <div className="flex items-start gap-4" data-id="element-178">
                <div className="bg-blue-100 p-3 rounded-full" data-id="element-179">
                  <Zap className="text-blue-600 w-6 h-6" data-id="element-180" />
                </div>
                <div data-id="element-181">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-182">
                    Querés sentirte seguro
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-183">
                    Frente a cualquier esquema que te presenten.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto" data-id="element-184">
            No importa si tenés años de experiencia o si estás creciendo en el
            oficio. Esto no reemplaza tu experiencia: la ordena y la potencia.
          </p>
        </div>
      </section>

      {/* Bonuses Section */}
      <section id="bonuses" className="py-20 px-4 bg-white" data-id="element-185">
        <div className="container mx-auto max-w-6xl" data-id="element-186">
          <div className="text-center mb-16" data-id="element-187">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-188">
              Llevate estos Bonos Exclusivos
            </h2>
            <p className="text-xl text-gray-600" data-id="element-189">
              Complementos perfectos para tu crecimiento profesional
            </p>
            <div className="mt-4 inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wide animate-pulse" data-id="element-190">
              ¡Gratis por el día de hoy!
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12" data-id="element-191">
            <div onClick={scrollToPurchase} className="cursor-pointer" data-id="element-192">
              <BonusCard number={1} image="https://cdn.magicpatterns.com/uploads/e919PomweLVKfukpiw3LMN/a5a9a24c-b864-40d1-a089-c1af5b14df2a.png" title="Eficiencia en Motores" description="Guía técnica para optimizar el rendimiento y productividad laboral." data-id="element-193" />
            </div>
            <div onClick={scrollToPurchase} className="cursor-pointer" data-id="element-194">
              <BonusCard number={2} image="https://cdn.magicpatterns.com/uploads/aG6G5fo2D5A3ofJLUDyPbN/3b26e923-1811-40cd-a322-fc64b4b38a41.png" title="Plantilla de Presupuesto" description="Formato editable para presentar cotizaciones profesionales y ganar más trabajos." data-id="element-195" />
            </div>
            <div onClick={scrollToPurchase} className="cursor-pointer" data-id="element-196">
              <BonusCard number={3} image="https://cdn.magicpatterns.com/uploads/kuzxSSrN4E2Rig37jDh6hQ/648be31b-ae21-445e-8f7f-8dab35c9db3e.png" title="Protocolo de Verificación" description="Checklist de seguridad previa a la energización para evitar accidentes." data-id="element-197" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-198">
        <div className="container mx-auto max-w-6xl" data-id="element-199">
          <h2 className="text-3xl font-bold text-center text-blue-900 mb-12" data-id="element-200">
            Lo que dicen nuestros colegas
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-201">
            <TestimonialCard name="Carlos Méndez" age={34} role="Electricista Industrial" location="Buenos Aires" content="Antes dudaba mucho al ver planos complejos. Este manual me dio la estructura mental que me faltaba. Ahora encaro los tableros con otra seguridad." data-id="element-202" />
            <TestimonialCard name="Roberto Silva" age={42} role="Técnico de Mantenimiento" location="Ciudad de México" content="Llevo 15 años en el rubro y pensé que sabía todo. Este libro me ordenó conceptos que usaba mal. Los bonos son excelentes, sobre todo la plantilla." data-id="element-203" />
            <TestimonialCard name="Juan Pérez" age={29} role="Electricista" location="Bogotá" content="La mejor inversión que hice este año. Explicaciones claras, sin vueltas. Me sirvió para un trabajo grande la semana pasada." data-id="element-204" />
            <TestimonialCard name="Miguel Torres" age={38} role="Técnico Eléctrico" location="Lima" content="El miedo a quemar algo siempre estaba. Entender la lógica de mando me quitó ese peso de encima. Muy recomendado." data-id="element-205" />
            <TestimonialCard name="Fernando González" age={45} role="Supervisor Eléctrico" location="Santiago" content="Compré el manual para mis técnicos y terminamos usándolo todos. Es una herramienta de consulta permanente en el taller." data-id="element-206" />
          </div>
        </div>
      </section>

      {/* Summary Section */}
      <section className="py-16 px-4 bg-white" data-id="element-207">
        <div className="container mx-auto max-w-3xl text-center" data-id="element-208">
          <h2 className="text-3xl font-bold text-blue-900 mb-8" data-id="element-209">
            Lo que te llevás con Control Eléctrico
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto mb-10" data-id="element-210">
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg" data-id="element-211">
              <Check className="text-green-500 w-6 h-6" data-id="element-212" />
              <span className="font-medium" data-id="element-213">
                Más seguridad frente a los diagramas
              </span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg" data-id="element-214">
              <Check className="text-green-500 w-6 h-6" data-id="element-215" />
              <span className="font-medium" data-id="element-216">Más criterio técnico</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg" data-id="element-217">
              <Check className="text-green-500 w-6 h-6" data-id="element-218" />
              <span className="font-medium" data-id="element-219">Menos errores</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg" data-id="element-220">
              <Check className="text-green-500 w-6 h-6" data-id="element-221" />
              <span className="font-medium" data-id="element-222">Menos dudas</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg md:col-span-2" data-id="element-223">
              <Check className="text-green-500 w-6 h-6" data-id="element-224" />
              <span className="font-medium" data-id="element-225">Más confianza profesional</span>
            </div>
          </div>
          <p className="text-xl text-gray-700 font-medium" data-id="element-226">
            Dejás de copiar sin entender. Dejás de depender de otros. Empezás a
            trabajar con lógica y tranquilidad.
          </p>
        </div>
      </section>

      {/* UPDATED Purchase Section - Aggressive Offer Format */}
      <section id="compra" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-227">
        <div className="container mx-auto max-w-4xl" data-id="element-228">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-center text-gray-900" data-id="element-229">
            Ahora es tu turno
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto text-center" data-id="element-230">
            Si querés entender de verdad el control eléctrico, trabajar con más
            seguridad y criterio, y dejar atrás la improvisación…
          </p>

          {/* Offer Card - Professional & Aggressive */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl max-w-lg mx-auto border-2 border-gray-200" data-id="element-231">
            {/* Strikethrough Price - Red */}
            <div className="text-center mb-4" data-id="element-232">
              <p className="text-red-600 font-bold text-xl" data-id="element-233">
                ¡Antes <span className="line-through" data-id="element-234">$49.900 ARS</span>!
              </p>
            </div>

            {/* Main Title */}
            <div className="text-center mb-6" data-id="element-235">
              <h3 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-2" data-id="element-236">
                SOLO POR HOY
              </h3>
            </div>

            {/* Product Name */}
            <h4 className="text-2xl font-bold text-center text-gray-800 mb-2" data-id="element-237">
              CONTROL ELÉCTRICO
            </h4>
            <p className="text-center text-gray-500 mb-6" data-id="element-238">
              + 3 Bonos de Regalo
            </p>

            {/* Main Price - Green & Huge */}
            <div className="text-center mb-6" data-id="element-239">
              <div className="inline-block bg-green-50 px-8 py-6 rounded-xl border-2 border-green-500" data-id="element-240">
                <p className="text-6xl md:text-7xl font-extrabold text-green-600" data-id="element-241">
                  $19.990
                </p>
                <p className="text-xl text-gray-600 font-medium mt-2" data-id="element-242">ARS</p>
              </div>
            </div>

            {/* Installments */}
            <p className="text-center text-lg font-semibold text-gray-700 mb-8" data-id="element-243">
              ¡Hasta 3 cuotas sin interés!
            </p>

            {/* CTA Button - Yellow/Orange */}
            <button onClick={handlePurchaseClick} className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-white text-xl md:text-2xl font-bold py-5 px-6 rounded-xl shadow-lg hover:shadow-2xl transition-all transform hover:scale-105 mb-6" data-id="element-244">
              SÍ, QUIERO LA OFERTA AHORA
            </button>

            {/* Micro-Trust Indicators */}
            <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 pt-6 border-t border-gray-200" data-id="element-245">
              <div className="flex items-center gap-2" data-id="element-246">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-247" />
                <span data-id="element-248">Acceso inmediato</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-249">
                <Download className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-250" />
                <span data-id="element-251">Descarga digital</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-252">
                <Smartphone className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-253" />
                <span data-id="element-254">Compatible con todos tus dispositivos</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-255">
                <Lock className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-256" />
                <span data-id="element-257">Pago 100% seguro</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Preview Section */}
      <section className="py-20 px-4 bg-white" data-id="element-258">
        <div className="container mx-auto max-w-6xl" data-id="element-259">
          <div className="text-center mb-12" data-id="element-260">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-261">
              Así es el material por dentro
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-262">
              Ejemplos reales de las páginas que vas a usar en el trabajo diario
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-263">
            {[{
            image: 'https://cdn.magicpatterns.com/uploads/6N9aigZzaNsoRe37pWdoFH/WhatsApp_Image_2026-01-17_at_13.42.51.jpg',
            caption: 'Diagramas explicados paso a paso'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/aRdF2bWiMHEmctuHXsJVR9/WhatsApp_Image_2026-01-17_at_13.42.50_(3).jpg',
            caption: 'Entendé la lógica del circuito, no solo la conexión'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/fuLknpKftbKZSC5DGAQ2tZ/WhatsApp_Image_2026-01-17_at_13.42.50_(2).jpg',
            caption: 'Casos prácticos aplicados al trabajo real'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/aGWc1fPM9DTj6kfR5oKjFr/WhatsApp_Image_2026-01-17_at_13.42.50_(1).jpg',
            caption: 'Material pensado para uso profesional'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/dZNF3VrgumsrSXiMRXLWTh/WhatsApp_Image_2026-01-17_at_13.42.49.jpg',
            caption: 'Explicaciones técnicas claras y directas'
          }].map((item, index) => <div key={index} className="bg-gray-50 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow" data-id="element-264">
                <div className="aspect-[3/4] relative" data-id="element-265">
                  <img src={item.image} alt={item.caption} className="w-full h-full object-cover" data-id="element-266" />
                </div>
                <div className="p-4" data-id="element-267">
                  <p className="text-sm font-medium text-gray-700 text-center" data-id="element-268">
                    {item.caption}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-16 px-4 bg-blue-50" data-id="element-269">
        <div className="container mx-auto max-w-3xl" data-id="element-270">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-2 border-blue-200" data-id="element-271">
            <div className="flex items-center justify-center mb-6" data-id="element-272">
              <div className="bg-blue-100 p-4 rounded-full" data-id="element-273">
                <Shield className="w-12 h-12 text-blue-600" data-id="element-274" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-center text-blue-900 mb-6" data-id="element-275">
              Garantía sin vueltas – 7 días
            </h3>
            <p className="text-lg text-gray-700 text-center leading-relaxed" data-id="element-276">
              Estamos tan seguros de que este material te va a aportar claridad
              real en tu trabajo, que podés probarlo durante{' '}
              <strong data-id="element-277">7 días sin riesgo</strong>. Si no te convence, nos
              escribís y te devolvemos el <strong data-id="element-278">100% del dinero</strong>. Así
              de simple.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white" data-id="element-279">
        <div className="container mx-auto max-w-3xl" data-id="element-280">
          <div className="text-center mb-12" data-id="element-281">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-282">
              Resolvé acá tus dudas
            </h2>
            <p className="text-lg text-gray-600" data-id="element-283">
              Hacé clic para ver la respuesta que necesitás
            </p>
          </div>

          <div className="space-y-3" data-id="element-284">
            <FAQItem question="¿Qué tipo de contenido incluye CONTROL ELÉCTRICO?" answer="Incluye un manual completo de control eléctrico, con explicación clara de la lógica de los circuitos, diagramas reales y ejemplos aplicados al trabajo profesional." data-id="element-285" />
            <FAQItem question="¿Este material es solo para electricistas con experiencia?" answer="Está pensado tanto para electricistas con experiencia como para técnicos que quieren entender de verdad el control eléctrico y dejar de trabajar por repetición o memoria." data-id="element-286" />
            <FAQItem question="¿Voy a aprender a interpretar diagramas eléctricos?" answer="Sí. El foco principal es ayudarte a interpretar y entender diagramas de mando y control eléctrico, no solo copiarlos." data-id="element-287" />
            <FAQItem question="¿Incluye ejemplos prácticos reales?" answer="Sí. El contenido está orientado a situaciones reales de tableros, motores y circuitos usados en el trabajo diario." data-id="element-288" />
            <FAQItem question="¿En qué formato recibo el material?" answer="Todo el contenido se entrega en formato digital (PDF) con acceso inmediato." data-id="element-289" />
            <FAQItem question="¿Puedo imprimir el material?" answer="Sí. Podés imprimirlo para uso personal." data-id="element-290" />
            <FAQItem question="¿Cuánto tarda en llegar el material después de la compra?" answer="El acceso es inmediato una vez acreditado el pago." data-id="element-291" />
            <FAQItem question="¿Necesito conocimientos avanzados de electrónica o automatización?" answer="No. El material explica los conceptos de forma clara y progresiva, enfocándose en criterio y lógica." data-id="element-292" />
            <FAQItem question="¿El contenido me sirve para trabajar con más seguridad?" answer="Sí. El objetivo es que entiendas qué está pasando en el circuito para trabajar con más seguridad y confianza." data-id="element-293" />
            <FAQItem question="¿Tengo acceso para siempre al material?" answer="Sí. El acceso es permanente." data-id="element-294" />
            <FAQItem question="¿Es un pago único?" answer="Sí. Pagás una sola vez y accedés a todo el contenido y bonos incluidos." data-id="element-295" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-sm" data-id="element-296">
        <div className="container mx-auto" data-id="element-297">
          <p data-id="element-298">
            &copy; {new Date().getFullYear()} Control Eléctrico. Todos los
            derechos reservados.
          </p>
          <p className="mt-2" data-id="element-299">
            Diagramas y circuitos de mando para profesionales.
          </p>
        </div>
      </footer>
    </div>;
}